import type { CmtsComplaintDetailVm, CmtsComplaintRecordVm, CmtsPriority, CmtsSeverity } from "../view-models/complaintViewModels.js";
import { enumOrUnknown, readArray, readBoolean, readDateIso, readString } from "./mapperUtils.js";
import { mapWorkflowAction } from "./workflowMappers.js";
import { mapEvidenceItem } from "./evidenceMappers.js";
import { mapAuditEvent, mapTraceabilityEvent } from "./diagnosticMappers.js";
import { mapTask } from "./taskMappers.js";
import { mapNotification } from "./notificationMappers.js";

export function mapComplaintRecord(dto: unknown): CmtsComplaintRecordVm {
  const severity = enumOrUnknown(readString(dto, ["severity", "severityCode"], "unknown").toLowerCase(), ["low", "medium", "high", "critical", "unknown"] as const, "unknown") as CmtsSeverity;
  const priority = enumOrUnknown(readString(dto, ["priority", "priorityCode"], "unknown").toLowerCase(), ["low", "medium", "high", "critical", "unknown"] as const, "unknown") as CmtsPriority;
  const id = readString(dto, ["id", "complaintId"], "");
  return {
    id,
    code: readString(dto, ["code", "complaintCode", "complaintNumber"], id || "Not assigned"),
complaintNumber: readString(
  dto,
  [
    "complaintNumber",
    "complaintCode",
    "registrationNumber",
    "referenceNumber",
    "caseNumber",
    "code",
  ],
  "",
),    title: readString(dto, ["title", "subject", "summary", "complaintTitle"], "Untitled complaint"),
    status: readString(dto, ["status", "statusCode"], "UNKNOWN"),
    statusLabel: readString(dto, ["statusLabel", "status", "statusCode"], "Unknown"),
    ownerId: readString(dto, ["ownerId", "assignedToUserId", "responsibleUserId"], "unassigned"),
    owner: readString(dto, ["owner", "ownerName", "assignedTo", "responsibleUserName"], "Unassigned"),
    complainant: readString(dto, ["complainant", "complainantName", "customerName", "supplierName"], "Not specified"),
    category: readString(dto, ["category", "categoryName", "categoryCode"], "Unclassified"),
    subcategory: readString(dto, ["subcategory", "subcategoryName", "subcategoryCode"], "Not specified"),
    severity,
    priority,
    sourceChannel: readString(dto, ["sourceChannel", "channel", "source"], "Not specified"),
    slaDueDate: readDateIso(dto, ["slaDueDate", "dueAt", "resolutionDueAtUtc"], ""),
    slaStatus: readString(dto, ["slaStatus", "slaHealth", "breachStatus"], "Not calculated"),
    confidentiality: readString(dto, ["confidentiality", "visibility", "classification"], "internal"),
    duplicateStatus: readString(dto, ["duplicateStatus", "duplicateFlag"], "Not checked"),
    reopened: readBoolean(dto, ["reopened", "isReopened"], false),
    repeatComplaint: readBoolean(dto, ["repeatComplaint", "isRepeat"], false),
    updatedAt: readDateIso(dto, ["updatedAt", "updatedAtUtc", "modifiedAtUtc"], ""),
  };
}

export function mapComplaintDetail(dto: unknown): CmtsComplaintDetailVm {
  const base = mapComplaintRecord(dto);
  return {
    ...base,
    description: readString(dto, ["description", "details", "problemDescription"], "No description provided"),
    responseLog: readString(dto, ["responseLog", "customerResponse", "communicationSummary"], "No response logged"),
    closureVerification: readString(dto, ["closureVerification", "verificationSummary"], "Not verified"),
    currentStage: readString(dto, ["currentStage", "workflowStage", "stage"], base.status),
    allowedActions: readArray(dto, ["allowedActions", "workflowActions"], mapWorkflowAction),
    evidence: readArray(dto, ["evidence", "evidenceItems"], mapEvidenceItem),
    auditEvents: readArray(dto, ["auditEvents", "audit"], mapAuditEvent),
    traceabilityEvents: readArray(dto, ["traceabilityEvents", "traceability"], mapTraceabilityEvent),
    tasks: readArray(dto, ["tasks"], mapTask),
    notifications: readArray(dto, ["notifications"], mapNotification),
    containmentRecords: readArray(dto, ["containmentRecords", "containment"], (row) => row as Record<string, unknown>),
    investigationRecords: readArray(dto, ["investigationRecords", "investigation"], (row) => row as Record<string, unknown>),
    closureRecords: readArray(dto, ["closureRecords", "closure"], (row) => row as Record<string, unknown>),
    reopenRecords: readArray(dto, ["reopenRecords", "reopen"], (row) => row as Record<string, unknown>),
    customerSupplierResponses: readArray(dto, ["customerSupplierResponses"], (row) => row as Record<string, unknown>),
    lessonsLearned: readArray(dto, ["lessonsLearned"], (row) => row as Record<string, unknown>),
    messages: readArray(dto, ["messages"], (row) => row as Record<string, unknown>),
    calendarEvents: readArray(dto, ["calendarEvents"], (row) => row as Record<string, unknown>),
    riskRating: readString(dto, ["riskRating"], "Not rated"),
    containmentStatus: readString(dto, ["containmentStatus"], "Not Required"),
    customerResponseStatus: readString(dto, ["customerResponseStatus"], "Not Required"),
    supplierResponseStatus: readString(dto, ["supplierResponseStatus"], "Not Required"),
    affectedQuantity: readString(dto, ["affectedQuantity"], ""),
    defectQuantity: readString(dto, ["defectQuantity"], ""),
    partNumber: readString(dto, ["partNumber"], ""),
    batchNumber: readString(dto, ["batchNumber"], ""),
    duplicateStatus: readString(dto, ["duplicateStatus"], base.duplicateStatus),
    diagnostics: [],
  };
}
